import { generateGuid } from 'guid';
import { type FrameInterface, type LessonIguanaObject, type InitFrameAttrs, type AuthorComment } from './Lessons.types';
import { FrameViewModel } from './FrameViewModel';

/*
    This class is used as a superclass for all non-iguana frames. While it is not
    directly a superclass for iguana frames, it behaves somewhat like it, because frame.js
    imports many of the methods from here into its prototype.

    We only define functionality in this file that is intended for use both in iguana
    and outside of it.
*/
export abstract class Frame implements FrameInterface {
    static FrameViewModel = FrameViewModel;
    public abstract directiveName: string;
    public abstract miniInstructions: string;
    public abstract buttonDirectiveName: string;
    public playerViewModel?: unknown;
    public supportsCustomFrameNavigation: boolean = false;
    public hasBranching: boolean = false;
    public newAuthorComment?: string;
    private _lesson?: LessonIguanaObject<true>;

    // oxlint-disable-next-line no-useless-constructor
    protected constructor(_initAttrs: InitFrameAttrs = {}) {
        // Abstract class constructor - implemented by subclasses
    }

    protected generateId(): string {
        return generateGuid();
    }

    abstract get id(): string;

    get lesson(): () => LessonIguanaObject<true> | undefined {
        return () => this._lesson;
    }

    setLesson(lesson: LessonIguanaObject<true>): void {
        this._lesson = lesson;
    }

    get isPractice(): boolean {
        return false;
    }

    set isPractice(_val: boolean) {
        throw new Error('NotImplemented');
    }

    get authorComments(): AuthorComment[] {
        throw new Error('NotImplemented');
    }

    set authorComments(_val: AuthorComment[]) {
        throw new Error('NotImplemented');
    }

    // See comment where savesProgressOnComplete is set in challenges_model.js
    get savesProgressOnComplete(): boolean {
        return false;
    }

    // Implemented this way so it can be re-used in frame.js
    get previousFrameIds(): string[] {
        return this.getPreviousFrameIds();
    }

    getPreviousFrameIds(): string[] {
        const lesson = this.lesson();
        if (!lesson) return [];
        const frameIds = lesson.frames.map(frame => frame.id);
        const index = frameIds.indexOf(this.id);
        if (index <= 0) return [];
        return frameIds.slice(0, index);
    }

    index(): number {
        const lesson = this.lesson();
        if (!lesson || !lesson.frames) {
            return -1;
        }
        return lesson.frames.indexOf(this as FrameInterface);
    }

    displayIndex(): number {
        return this.index() + 1;
    }

    logInfo(): Record<string, unknown> {
        return {
            frame_id: this.id,
            frame_index: this.index(),
            editor_template:
                (this as { mainUiComponent?: { editor_template?: string } }).mainUiComponent?.editor_template ?? null,
        };
    }

    // see also: frame_list.js:reify()
    reify(): this {
        return this;
    }

    get mainImage(): unknown {
        return undefined;
    }

    set mainImage(_value: unknown) {
        throw new Error('NotImplemented');
    }

    preloadAssets() {
        return this.preloadImages();
    }

    preloadImages(): Promise<void> {
        return Promise.resolve();
    }

    prevFrame(): FrameInterface | undefined {
        const lesson = this.lesson();
        if (!lesson || !lesson.frames) {
            return undefined;
        }
        const currentIndex = this.index();
        return currentIndex > 0 ? (lesson.frames[currentIndex - 1] as FrameInterface) : undefined;
    }

    nextFrame(): FrameInterface | undefined {
        const lesson = this.lesson();
        if (!lesson || !lesson.frames) {
            return undefined;
        }
        const currentIndex = this.index();
        return currentIndex < lesson.frames.length - 1
            ? (lesson.frames[currentIndex + 1] as FrameInterface)
            : undefined;
    }

    createFrameViewModel(options?: unknown): unknown {
        const FrameViewModelClass = (this.constructor as typeof Frame).FrameViewModel;
        if (!FrameViewModelClass) {
            throw new Error('FrameViewModel not defined for this frame type');
        }
        return new FrameViewModelClass(this, options as Record<string, unknown>);
    }

    get text_content(): string {
        return '';
    }

    containsText(text: string): boolean {
        return !!(text && this.text_content && this.text_content.toLowerCase().includes(text.toLowerCase()));
    }

    getKeyTerms(): unknown[] {
        return [];
    }

    imageForLabel(_label: string): unknown {
        throw new Error('NotImplemented: imageForLabel should be implemented in subclasses');
    }

    deleteComment(index: number): void {
        this.authorComments.splice(index, 1);
    }

    addComment(currentUser: { id: string; name: string }) {
        if (!this.newAuthorComment?.trim()) {
            return;
        }

        const comment: AuthorComment = {
            text: this.newAuthorComment,
            timestamp: new Date().getTime(),
            author_id: currentUser.id,
            author_name: currentUser.name,
            completed: false,
        };

        if (!this.authorComments) {
            this.authorComments = [];
        }

        this.authorComments.push(comment);

        this.newAuthorComment = undefined;
    }

    toggleCommentCompleted(index: number) {
        const comment = this.authorComments[index];
        comment.completed = !comment.completed;
    }

    duplicate(): FrameInterface {
        throw new Error('NotImplemented: duplicate should be implemented in subclasses');
    }

    mightNavigateTo(frame: FrameInterface): boolean {
        if (this.supportsCustomFrameNavigation) {
            throw new Error('Subclasses of Frame with supportsCustomFrameNavigation should implement mightNavigateTo');
        }

        return frame.index() === this.index() + 1;
    }

    expandExtraPanelsInitially(): void {
        // No op
    }

    recursivelyGetImageUrls(): Promise<string[]> {
        return Promise.resolve([]);
    }

    replaceFrameReferences(_oldFrame: FrameInterface, _newFrame: FrameInterface): void {
        // No op
    }

    copyMainTextAndImagesFrom(_otherFrame: FrameInterface | undefined | null): void {
        // No op
    }

    formatAllText(_force: boolean = false) {
        // No op
    }
}
